import { useEffect, useState } from "react";
import { StickyNote, Plus, X, Loader2, Eraser } from "lucide-react";
import { personalTodosApi } from "@/lib/api";
import { toast } from "sonner";

// StickiesWidget — Feb 2026.
//
// Per-user quick-capture notes that live on Launchpad. Not project-related,
// no due dates, no editing (yet), no history. The "minimum viable
// scratchpad" — type, hit Enter, check it off, clear completed.

const MAX_TEXT = 280;

export default function StickiesWidget() {
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("");
  const [adding, setAdding] = useState(false);

  const load = async () => {
    try {
      const data = await personalTodosApi.list();
      setTodos(data || []);
    } catch (e) {
      // Silent fail — widget shouldn't break Launchpad if it's down
      console.error("Stickies load failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleAdd = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text) return;
    if (text.length > MAX_TEXT) {
      toast.error(`Too long — keep it under ${MAX_TEXT} characters`);
      return;
    }
    setAdding(true);
    // Optimistic insert
    const temp = {
      id: `__tmp_${Date.now()}`,
      text,
      completed: false,
      created_at: new Date().toISOString(),
    };
    setTodos((prev) => [temp, ...prev]);
    setInput("");
    try {
      const created = await personalTodosApi.create(text);
      setTodos((prev) => prev.map((t) => (t.id === temp.id ? created : t)));
    } catch (err) {
      setTodos((prev) => prev.filter((t) => t.id !== temp.id));
      toast.error(err?.response?.data?.detail || "Failed to add note");
    } finally {
      setAdding(false);
    }
  };

  const handleToggle = async (todo) => {
    const next = !todo.completed;
    setTodos((prev) =>
      prev.map((t) =>
        t.id === todo.id
          ? { ...t, completed: next, completed_at: next ? new Date().toISOString() : null }
          : t,
      ),
    );
    try {
      await personalTodosApi.toggle(todo.id, next);
    } catch {
      // Revert
      setTodos((prev) =>
        prev.map((t) => (t.id === todo.id ? { ...t, completed: !next } : t)),
      );
      toast.error("Failed to update");
    }
  };

  const handleDelete = async (todo) => {
    const prev = todos;
    setTodos((cur) => cur.filter((t) => t.id !== todo.id));
    try {
      await personalTodosApi.remove(todo.id);
    } catch {
      setTodos(prev);
      toast.error("Failed to delete");
    }
  };

  const handleClearCompleted = async () => {
    const completedCount = todos.filter((t) => t.completed).length;
    if (completedCount === 0) return;
    const prev = todos;
    setTodos((cur) => cur.filter((t) => !t.completed));
    try {
      const res = await personalTodosApi.clearCompleted();
      toast.success(`Cleared ${res.deleted} completed`);
    } catch {
      setTodos(prev);
      toast.error("Failed to clear");
    }
  };

  const completedCount = todos.filter((t) => t.completed).length;
  const remaining = MAX_TEXT - input.length;

  return (
    <article
      data-testid="stickies-widget"
      className="border border-zinc-800 bg-zinc-950/60 rounded-md p-6 mb-5"
      style={{ borderLeftColor: "#fbbf24", borderLeftWidth: 3 }}
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="text-overline text-amber-500/80 mb-1 flex items-center gap-1.5">
            <StickyNote className="w-3 h-3" /> Stickies · just for you
          </div>
          <h2 className="font-heading text-xl font-light text-zinc-100 leading-tight">
            Quick notes
          </h2>
          <p className="text-xs text-zinc-500 mt-1">
            Personal scratchpad. Nobody else sees these.
          </p>
        </div>
        {completedCount > 0 && (
          <button
            type="button"
            data-testid="stickies-clear-completed"
            onClick={handleClearCompleted}
            className="text-xs text-zinc-500 hover:text-amber-400 flex items-center gap-1 transition-colors"
            title="Delete all checked-off notes"
          >
            <Eraser className="w-3 h-3" /> Clear {completedCount} done
          </button>
        )}
      </div>

      {/* Add input */}
      <form onSubmit={handleAdd} className="flex gap-2 mb-4">
        <input
          data-testid="stickies-input"
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value.slice(0, MAX_TEXT))}
          placeholder="Add a quick note… (Enter to save)"
          maxLength={MAX_TEXT}
          className="flex-1 bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/60"
          disabled={adding}
        />
        <button
          type="submit"
          data-testid="stickies-add-btn"
          disabled={!input.trim() || adding}
          className="bg-amber-500 text-black hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed rounded-sm px-3 transition-colors flex items-center gap-1 text-sm font-medium"
        >
          {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
        </button>
      </form>
      {input.length > MAX_TEXT - 40 && (
        <p className="text-[10px] text-zinc-600 -mt-3 mb-2">
          {remaining} characters left
        </p>
      )}

      {/* List */}
      {loading ? (
        <div className="flex items-center gap-2 text-zinc-500 text-sm py-4">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : todos.length === 0 ? (
        <div className="text-xs italic text-zinc-600 py-4">
          No notes yet. Type one above to get started.
        </div>
      ) : (
        <ul className="space-y-1.5" data-testid="stickies-list">
          {todos.map((t) => (
            <li
              key={t.id}
              data-testid={`sticky-${t.id}`}
              className={`group flex items-start gap-2.5 py-1.5 ${
                t.completed ? "opacity-50" : ""
              }`}
            >
              <button
                type="button"
                data-testid={`sticky-toggle-${t.id}`}
                onClick={() => handleToggle(t)}
                className={`mt-0.5 w-4 h-4 rounded-sm border shrink-0 transition-colors flex items-center justify-center ${
                  t.completed
                    ? "bg-amber-500 border-amber-500 text-black"
                    : "border-zinc-700 hover:border-amber-500"
                }`}
                aria-label={t.completed ? "Mark incomplete" : "Mark complete"}
              >
                {t.completed && (
                  <svg className="w-3 h-3" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8.5L7 12L13 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
              <span
                className={`flex-1 text-sm leading-snug break-words ${
                  t.completed ? "line-through text-zinc-500" : "text-zinc-200"
                }`}
              >
                {t.text}
              </span>
              <button
                type="button"
                data-testid={`sticky-delete-${t.id}`}
                onClick={() => handleDelete(t)}
                className="opacity-0 group-hover:opacity-100 text-zinc-600 hover:text-rose-400 transition-all shrink-0"
                aria-label="Delete note"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}
